import ArrowForwardIcon from "@mui/icons-material/ArrowForwardRounded";
import LightbulbIcon from "@mui/icons-material/LightbulbOutlined";
import { Box, Stack, Typography, alpha } from "@mui/material";
import { useProjectStore } from "@stores/projectStore";
import { useMemo } from "react";

import { CATEGORY_BY_KEY } from "../categoryCatalog";
import type { CategoryKey } from "../types";

/**
 * Chat-side summary card for a batch of `propose_requirement` calls in
 * one turn. Replaces the old per-proposal accept/reject card — the
 * agent now writes each suggestion directly into the requirements list
 * at status='suggested', and the user reviews them there with full
 * context (category section, neighbouring requirements, edit-in-place).
 *
 * One line, low visual weight: count + categories touched + a click
 * target that opens the Requirements page. We deliberately don't
 * surface the statement bodies here — that's the list's job. Showing
 * the same content twice is what created the original bulk-accept
 * scrolling problem.
 */

export interface ProposalSummary {
  category: CategoryKey;
  statement: string;
  requirementId?: string;
}

interface Props {
  proposals: ProposalSummary[];
}

export function ProposeBatchCard({ proposals }: Props) {
  const projectId = useProjectStore((s) => s.activeProject);

  const categoryLabels = useMemo(() => {
    const seen = new Set<string>();
    const labels: string[] = [];
    for (const p of proposals) {
      const label =
        CATEGORY_BY_KEY[p.category]?.label.toLowerCase() ?? p.category;
      if (!seen.has(label)) {
        seen.add(label);
        labels.push(label);
      }
    }
    return labels;
  }, [proposals]);

  if (proposals.length === 0) return null;

  const count = proposals.length;
  const noun = count === 1 ? "requirement" : "requirements";
  const categoryText =
    categoryLabels.length === 0
      ? ""
      : ` in ${formatList(categoryLabels)}`;

  const href = projectId
    ? `/projects/${encodeURIComponent(projectId)}/requirements`
    : undefined;

  return (
    <Box
      component={href ? "a" : "div"}
      href={href}
      sx={(t) => ({
        mt: 1,
        display: "flex",
        alignItems: "center",
        gap: 1,
        px: 1.5,
        py: 1,
        borderRadius: 1.5,
        textDecoration: "none",
        color: "inherit",
        background: alpha(t.palette.editorial?.main ?? "#d4a05c", 0.08),
        border: `1px solid ${alpha(
          t.palette.editorial?.main ?? "#d4a05c",
          0.36,
        )}`,
        cursor: href ? "pointer" : "default",
        transition: `background ${t.motion?.fast ?? "120ms"}`,
        "&:hover": href
          ? {
              background: alpha(t.palette.editorial?.main ?? "#d4a05c", 0.14),
            }
          : undefined,
      })}
    >
      <LightbulbIcon
        sx={(t) => ({
          fontSize: 16,
          color: t.palette.editorial?.main ?? "#d4a05c",
          flexShrink: 0,
        })}
      />
      <Stack
        direction="row"
        spacing={0.75}
        alignItems="baseline"
        sx={{ flex: 1, minWidth: 0 }}
      >
        <Typography
          variant="caption"
          sx={(t) => ({
            fontFamily: t.fonts?.mono ?? "monospace",
            fontSize: "0.72rem",
            fontVariantNumeric: "tabular-nums",
            color: t.palette.editorial?.main ?? "#d4a05c",
            fontWeight: 700,
            letterSpacing: "0.04em",
          })}
        >
          {count} suggested {noun}
        </Typography>
        <Typography
          variant="caption"
          sx={{
            fontSize: "0.72rem",
            color: "text.secondary",
            lineHeight: 1.45,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            flex: 1,
            minWidth: 0,
          }}
        >
          {categoryText} — review in list
        </Typography>
      </Stack>
      {href && (
        <ArrowForwardIcon
          sx={(t) => ({
            fontSize: 14,
            color: t.palette.editorial?.main ?? "#d4a05c",
            flexShrink: 0,
          })}
        />
      )}
    </Box>
  );
}

function formatList(items: string[]): string {
  if (items.length <= 1) return items.join("");
  if (items.length === 2) return items.join(" + ");
  return items.slice(0, -1).join(", ") + ` + ${items[items.length - 1]}`;
}
